import { format as d3format } from 'd3-format';

import { getMostSignificantDigit } from './get-most-significant-digit';

const MAX_SI_EXPONENT = 27;
const MIN_SI_EXPONENT = -27;
const MAX_REASONABLE_PRECISION = 17; // At most 17 digits can be captured by double precision.

const trailingZerosRe = /(?:[.]|(?<=[.]\d*[1-9]\d*))0+(?:$|(?=e))/;

export function removeTrailingZeros(value: string) {
  return value.replace(trailingZerosRe, '');
}

export function toFixedWithSI(
  value: number,
  lastSignificantDigit: number,
  minSignificantDigits = 1,
): string {
  if (!Number.isFinite(value) || value === 0) {
    // !Number.isFinite(value). Never print extra digits for zero.
    return value.toString();
  }

  const mostSignificantDigit = getMostSignificantDigit(value);

  const effectivePrecision = Math.min(
    Math.max(mostSignificantDigit - lastSignificantDigit + 1, minSignificantDigits),
    MAX_REASONABLE_PRECISION,
  );
  const effectiveLastDigit = mostSignificantDigit - effectivePrecision + 1;

  if (
    !Number.isFinite(lastSignificantDigit) ||
    !Number.isFinite(mostSignificantDigit) ||
    !Number.isFinite(effectivePrecision) ||
    !Number.isFinite(effectiveLastDigit)
  ) {
    // Fallback for when we can't calculate the precision correctly - a last resort safety net.
    return removeTrailingZeros(value.toPrecision(MAX_REASONABLE_PRECISION));
  }

  if (effectiveLastDigit <= 0 && effectiveLastDigit >= -3) {
    return removeTrailingZeros(value.toFixed(-effectiveLastDigit));
  }

  if (mostSignificantDigit < 100 && effectiveLastDigit >= 0 && effectiveLastDigit < 3) {
    return removeTrailingZeros(value.toFixed(0));
  }

  if (mostSignificantDigit > MAX_SI_EXPONENT || mostSignificantDigit < MIN_SI_EXPONENT) {
    return removeTrailingZeros(value.toPrecision(effectivePrecision));
  }

  if ((mostSignificantDigit > -3 && effectiveLastDigit <= 0) || mostSignificantDigit === -3) {
    // Don't use SI prefixes if decimal point is between significant digits.
    // Don't use m prefix.
    return removeTrailingZeros(value.toFixed(-effectiveLastDigit));
  }

  if (mostSignificantDigit >= 0 && effectiveLastDigit < 2 && effectiveLastDigit >= 0) {
    // Don't use large SI prefixes if they wouldn't shorten the number.
    return removeTrailingZeros(value.toFixed(0));
  }

  // SI notation without trailing zeros
  return d3format(`.${effectivePrecision}~s`)(value);
}
